import { css } from '@emotion/react'
import { Cell, Flow, Grid } from 'bold-ui'
import { Box } from 'components/Box'
import { RadioGroupField } from 'components/form'
import { CheckboxGroupField } from 'components/form/field/CheckboxGroupField'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { InfoIcon } from 'components/InfoIcon'
import { RastreioCancerPerguntaOpcaoEnum } from 'graphql/types.generated'
import { ChangeEvent } from 'react'
import { useField, useForm } from 'react-final-form'

import { AnoSelectField } from '../citopatologico/AnoSelectField'
import { SimNaoNaoSabeRadioField } from '../citopatologico/SimNaoNaoSabeRadioField'
import { MamografiaAnamneseCirurgiaTable } from './MamografiaAnamneseCirurgiaTable'
import {
  localizacaoRadioterapiaOptions,
  mamasExaminadasOptions,
  MamografiaPerguntaField,
  noduloCarocoOptions,
  RISCO_CANCER_MAMA_TOOLTIP_TEXT,
} from './model-mamografia'

export function MamografiaAnamneseModalStep() {
  const {
    input: { value: fezRadioterapia },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(MamografiaPerguntaField.fezRadioterapia, {
    subscription: { value: true },
  })

  const {
    input: { value: localizacao },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(MamografiaPerguntaField.radioterapiaLocalizacao, {
    subscription: { value: true },
  })

  const form = useForm()

  const showRadioterapia = fezRadioterapia === RastreioCancerPerguntaOpcaoEnum.SIM
  const habilitaDireita =
    localizacao === RastreioCancerPerguntaOpcaoEnum.DIREITA || localizacao === RastreioCancerPerguntaOpcaoEnum.AMBAS
  const habilitaEsquerda =
    localizacao === RastreioCancerPerguntaOpcaoEnum.ESQUERDA || localizacao === RastreioCancerPerguntaOpcaoEnum.AMBAS

  const handleLocalizacaoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const novaLocalizacao = event.target.value
    if (novaLocalizacao === RastreioCancerPerguntaOpcaoEnum.DIREITA) {
      form.change(MamografiaPerguntaField.anoRadioterapiaMamaEsquerda, undefined)
    } else if (novaLocalizacao === RastreioCancerPerguntaOpcaoEnum.ESQUERDA) {
      form.change(MamografiaPerguntaField.anoRadioterapiaMamaDireita, undefined)
    }
  }

  return (
    <Grid>
      <Cell size={12}>
        <CheckboxGroupField
          name={MamografiaPerguntaField.noduloCaroco}
          label='Tem nódulo ou caroço na mama?'
          options={noduloCarocoOptions}
          required
        />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField
          label={
            <Flow direction='horizontal' gap={0.25}>
              <RequiredLabel inline fontWeight='bold' label='Apresenta risco elevado para câncer de mama?' />
              <InfoIcon icon='infoCircleFilled' text={RISCO_CANCER_MAMA_TOOLTIP_TEXT} tooltipStyle={styles.tooltip} />
            </Flow>
          }
          name={MamografiaPerguntaField.riscoCancerMama}
        />
      </Cell>
      <Cell size={12}>
        <RadioGroupField
          name={MamografiaPerguntaField.mamasExaminadas}
          label='Antes desta consulta, teve suas mamas examinadas por um profissional de saúde?'
          options={mamasExaminadasOptions}
          required
        />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField
          name={MamografiaPerguntaField.fezMamografia}
          label='Fez mamografia alguma vez?'
          required
        />
      </Cell>
      <Cell size={6}>
        <AnoSelectField
          name={MamografiaPerguntaField.anoUltimaMamografia}
          label='Quando fez a última mamografia?'
          placeholder='Ano da última mamografia'
          required
        />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField
          name={MamografiaPerguntaField.fezRadioterapia}
          label='Fez radioterapia na mama ou no plastão?'
          required
        />
      </Cell>
      {showRadioterapia && (
        <Cell size={12}>
          <Box>
            <Grid>
              <Cell size={12}>
                <RadioGroupField
                  name={MamografiaPerguntaField.radioterapiaLocalizacao}
                  label='Radioterapia - Localização'
                  options={localizacaoRadioterapiaOptions}
                  onChange={handleLocalizacaoChange}
                  required
                />
              </Cell>
              <Cell size={6}>
                <AnoSelectField
                  name={MamografiaPerguntaField.anoRadioterapiaMamaDireita}
                  label='Ano da radioterapia na mama direita'
                  disabled={!habilitaDireita}
                  required={habilitaDireita}
                />
              </Cell>
              <Cell size={6}>
                <AnoSelectField
                  name={MamografiaPerguntaField.anoRadioterapiaMamaEsquerda}
                  label='Ano da radioterapia na mama esquerda'
                  disabled={!habilitaEsquerda}
                  required={habilitaEsquerda}
                />
              </Cell>
            </Grid>
          </Box>
        </Cell>
      )}
      <MamografiaAnamneseCirurgiaTable />
    </Grid>
  )
}

const styles = {
  tooltip: css`
    white-space: pre-wrap;
    max-width: 40rem;
  `,
}
